import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { Button } from '@sunfish/ui';
import { useForm } from 'react-hook-form';
import { useSearchParams, Link } from 'react-router';
import { Form } from '@sunfish/ui';
import { AuthLayout } from '~/components/auth';
import { InputBlock } from '@sunfish/forms';
import { Alert } from '@sunfish/ui';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from '@sunfish/ui';
import { adminAuthClient } from '~/lib/auth/admin-client';
import type { Provider } from '~/lib/auth/admin-config.server';
import { z } from 'zod';
import googleIcon from '~assets/img/providers/google-icon.svg';

const providers: { name: string; value: Provider; icon: string }[] = [
	{ name: 'Google', value: 'google', icon: googleIcon }
];
const emailPasswordEnabled = true;

const formSchema = z.object({
	email: z.string().email('Invalid email address').min(1, 'Email is required'),
	password: z.string()
});

// Belt-and-suspenders open-redirect guard. tenant-layout already filters next
// before adding it to the URL, but a hand-crafted /login?next=... could land
// here from anywhere, so we re-validate before handing the value to Better Auth.
function safeCallback(next: string | null): string {
	if (!next) return '/events';
	if (!next.startsWith('/') || next.startsWith('//')) return '/events';
	if (next === '/login' || next.startsWith('/login?')) return '/events';
	return next;
}

export default function LoginPage() {
	const [loading, setLoading] = useState(false);
	const [searchParams] = useSearchParams();
	const isUnauth = searchParams.get('unauth') !== null;
	const callbackURL = safeCallback(searchParams.get('next'));

	const form = useForm<z.output<typeof formSchema>>({
		mode: 'onBlur',
		resolver: zodResolver(formSchema)
	});

	const handleOAuthSignIn = async (provider: Provider) => {
		setLoading(true);
		try {
			const response = await adminAuthClient.signIn.social({
				provider,
				callbackURL
			});
			if (response.error) {
				throw new Error(response.error.message?.length ? response.error.message : 'An unexpected error occurred');
			}
		} catch (error) {
			form.setError('root', {
				type: 'manual',
				message: error instanceof Error ? error.message : 'An unexpected error occurred'
			});
		} finally {
			setLoading(false);
		}
	};

	const onSubmit = async (data: z.output<typeof formSchema>) => {
		setLoading(true);

		try {
			const response = await adminAuthClient.signIn.email({
				email: data.email,
				password: data.password,
				callbackURL
			});
			if (response.error) {
				throw new Error(response.error.message);
			}
		} catch (error) {
			form.setError('root', {
				type: 'manual',
				message: error instanceof Error ? error.message : 'An unexpected error occurred'
			});
		}

		setLoading(false);
	};

	return (
		<AuthLayout>
			<Form {...form}>
				<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
					<Card variant="divided" className={'min-w-[500px]'}>
						<CardHeader>
							<CardTitle>Login</CardTitle>
						</CardHeader>
						<CardContent className="space-y-4">
							{isUnauth && (
								<Alert variant="destructive" className="mb-4">
									<span className="text-sm">You are not authorized to access this page.</span>
								</Alert>
							)}
							{form.formState.errors?.root && (
								<Alert variant="destructive" className="mb-4">
									<span className="text-sm text-destructive">{form.formState.errors.root.message}</span>
								</Alert>
							)}

							{emailPasswordEnabled && (
								<>
									<InputBlock
										name="email"
										label="Email"
										type="email"
										control={form.control}
										required
										disabled={loading}
									/>
									<InputBlock
										name="password"
										label="Password"
										type="password"
										control={form.control}
										required
										disabled={loading}
									/>
									<div>
										<Link to="/forgot-password" className="text-sm font-medium text-brand hover:underline">
											Forgot your password?
										</Link>
									</div>
								</>
							)}
						</CardContent>

						<CardFooter>
							<div className="space-y-4 w-full">
								<Button
									disabled={loading}
									type="submit"
									variant="brand"
									size="lg"
									className="w-full"
									onClick={form.handleSubmit(onSubmit)}
								>
									{loading ? 'Signing in...' : 'Sign in with Email'}
								</Button>

								{providers?.length > 0 && (
									<>
										<div className="flex gap-2 items-center">
											<div className="w-full border-t border-border" />
											<span className="text-sm text-muted-foreground min-w-[120px] text-center">or continue with</span>
											<div className="w-full border-t border-border" />
										</div>
										<div className="flex flex-col gap-4 w-full">
											{providers.map((provider) => (
												<Button
													type="button"
													key={provider.value}
													variant="outline"
													size="lg"
													onClick={() => {
														handleOAuthSignIn(provider.value);
													}}
													disabled={loading}
												>
													<div className="mr-2 h-5 w-5">
														<img src={provider.icon} alt={provider.name} />
													</div>
													Sign in with {provider.name}
												</Button>
											))}
										</div>
									</>
								)}
							</div>
						</CardFooter>
					</Card>
				</form>
			</Form>
		</AuthLayout>
	);
}
